import {FC, useMemo, useRef, useCallback, useState, useContext} from "react";
import {
	Box,
	LinearProgress,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	DataGridPro,
	GridColDef,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridSelectionModel
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {useNavigate} from "react-router-dom";
import {useDeleteQuiz, type QuizListRow} from "@/app2/shared/data";
import {
	App2NotificationContext,
	App2NotificationSeverity
} from "@/app2/shared/contexts/App2NotificationContext";
import {useQuizzesListGrid} from "./hooks/useQuizzesListGrid";
import {QuizzesListGridToolbar} from "./Toolbar/QuizzesListGridToolbar";
import {useGetI18nLocales} from "@/app2/shared/data";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";
import {serverSideDateOnlyFilterOperators} from "@/app2/shared/dataGrid/serverSideDateTimeColumnFilters";

/** Native date filter: style only the picker glyph (WebKit/Blink), scoped to the Value field via filterFormProps. */
const quizzesFilterPanelProps = {
	filterFormProps: {
		valueInputProps: {
			sx: {
				"& input[type='date']::-webkit-calendar-picker-indicator, & .MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator":
					{
						filter: "invert(1)",
						opacity: 1,
						cursor: "pointer"
					},
				"& input[type='datetime-local']::-webkit-calendar-picker-indicator, & .MuiInputBase-input[type='datetime-local']::-webkit-calendar-picker-indicator":
					{
						filter: "invert(1)",
						opacity: 1,
						cursor: "pointer"
					}
			}
		}
	}
};

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		// Container border, single/thin dividers, and outer padding come from adminTheme MuiDataGrid
		// (root border, cell borderTop, root padding). Do not override border, padding, or columnHeaders/cell borders.
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& svg path": {
			fill: theme.palette.common.white
		}
	}
}));

export const QuizzesListGrid: FC = () => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const deleteQuiz = useDeleteQuiz();
	const {notify} = useContext(App2NotificationContext);
	const buttonContainerRef = useRef<HTMLDivElement>(null);
	const {data: localesData} = useGetI18nLocales();
	const locales = localesData?.data ?? [];

	const {
		rows,
		rowCount,
		isLoading,
		refetch,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	} = useQuizzesListGrid();

	const [deleteTarget, setDeleteTarget] = useState<QuizListRow | null>(null);
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

	const onRowClick = useCallback(
		(params: { id: number }, event: React.MouseEvent) => {
			if (buttonContainerRef.current?.contains(event.target as Node)) return;
			navigate(`${params.id}`);
		},
		[navigate]
	);

	const handleDeleteClick = useCallback(
		(e: React.MouseEvent, row: QuizListRow) => {
			e.stopPropagation();
			setDeleteTarget(row);
		},
		[]
	);

	const handleDeleteConfirm = useCallback(async () => {
		if (!deleteTarget) return;
		try {
			await deleteQuiz.mutateAsync({ quizId: deleteTarget.id });
			setDeleteTarget(null);
			setSelectionModel((prev) => prev.filter((id) => id !== deleteTarget.id));
			refetch();
			notify(
				"Quiz deleted successfully",
				"Success",
				App2NotificationSeverity.success,
				3000
			);
		} catch (err) {
			console.error("Delete quiz failed", err);
			const msg =
				err && typeof err === "object" && "message" in err && typeof (err as Error).message === "string"
					? (err as Error).message
					: "Failed to delete quiz";
			notify(msg, "Error", App2NotificationSeverity.error, 5000);
		}
	}, [deleteTarget, deleteQuiz, refetch, notify]);

	const handleDeleteCancel = useCallback(() => {
		setDeleteTarget(null);
	}, []);

	const columns: GridColDef<QuizListRow>[] = useMemo(
		() => [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				hideable: false
			},
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: "Quiz",
				minWidth: 240
			},
			{
				field: "publishedAt",
				type: "date",
				flex: 1,
				headerName: "Published",
				width: 100,
				filterOperators: serverSideDateOnlyFilterOperators,
				valueGetter: (params) => {
					if (!params.row.publishedAt) return null;
					const d = new Date(params.row.publishedAt);
					return Number.isNaN(d.getTime()) ? null : d;
				},
				valueFormatter: (params) => {
					const v = params.value as Date | null;
					if (!v || !(v instanceof Date) || Number.isNaN(v.getTime())) return "";
					return formatDateForDisplay(v);
				}
			},
			{
				field: "languages",
				type: "string",
				filterable: false,
				flex: 1,
				headerName: "Supported Languages",
				valueGetter: (params) =>
					(params.row.languages ?? [])
						?.map((code: string) => locales.find((l) => l.code === code)?.name ?? code)
						?.join(", ") ?? ""
			},
			{
				field: "actions",
				headerName: "",
				width: 56,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: (params) => (
					<Box ref={buttonContainerRef} onClick={(e) => e.stopPropagation()}>
						<IconButton
							size="small"
							aria-label="Delete quiz"
							onClick={(e) => handleDeleteClick(e, params.row)}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				)
			}
		],
		[handleDeleteClick, locales]
	);

	const Toolbar = useCallback(() => <QuizzesListGridToolbar />, []);

	if (isLoading && !rows.length) {
		return <LinearProgress />;
	}

	return (
		<>
			<Box>
				<DataGridPro<QuizListRow>
					autoHeight
					className={classes.dataGrid}
					loading={isLoading}
					sortingMode="server"
					page={page}
					pageSize={pageSize}
					onPageSizeChange={onPageSizeChange}
					onPageChange={onPageChange}
					checkboxSelection
					keepNonExistentRowsSelected
					selectionModel={selectionModel}
					onSelectionModelChange={setSelectionModel}
					paginationMode="server"
					pagination
					rowCount={rowCount}
					onSortModelChange={onSortModelChange}
					rows={rows}
					columns={columns}
					filterMode="server"
					onFilterModelChange={onFilterChange}
					components={{ Toolbar }}
					componentsProps={{filterPanel: quizzesFilterPanelProps}}
					rowsPerPageOptions={[25, 50, 100, 200]}
					initialState={defaultInitialState}
					onRowClick={(params, event) => onRowClick({ id: params.id as number }, event)}
					getRowId={(row) => row.id}
					disableSelectionOnClick
					sx={{
						"& .MuiDataGrid-row": {
							cursor: "pointer"
						}
					}}
				/>
				{isLoading && <LinearProgress />}
			</Box>
			<Dialog open={!!deleteTarget} onClose={handleDeleteCancel}>
				<DialogTitle>Delete quiz</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{color: "common.white"}}>
						Are you sure you want to delete this quiz?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel}>Cancel</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						variant="contained"
						disabled={deleteQuiz.isPending}
					>
						{deleteQuiz.isPending ? "Deleting…" : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
